import type { ReactNode } from "react";
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/24/solid";
import Button from "components/Button";
import { useScrollPageToTop } from "context/PageScrollContext";

type ArrangementNavProps = {
  onBack?: () => void;
  onContinue?: () => void;
  backLabel?: string;
  continueLabel?: string;
  // Small line rendered directly above the continue button (e.g. a "what's
  // next" breadcrumb), kept local to the button rather than floating away.
  continueHint?: ReactNode;
  showBack?: boolean;
  disableContinue?: boolean;
  // Set while a continue action is still in flight. Leaving Back live would let
  // the family navigate away from the step the pending action is about to
  // advance, landing that advance on whatever step they moved to.
  disableBack?: boolean;
};

export default function ArrangementNav({
  onBack,
  onContinue,
  backLabel = "Back",
  continueLabel = "Continue",
  continueHint,
  showBack = true,
  disableContinue = false,
  disableBack = false,
}: ArrangementNavProps) {
  const scrollPageToTop = useScrollPageToTop();

  const handleBack = () => {
    scrollPageToTop();
    onBack?.();
  };

  const handleContinue = () => {
    scrollPageToTop();
    onContinue?.();
  };

  return (
    <>
      {/* Mobile (below sm): fixed bottom bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-oat bg-white px-4 py-3 sm:hidden">
        {continueHint && (
          <p className="mb-2 text-sm text-soil">{continueHint}</p>
        )}
        <div className="flex items-center justify-between">
          {showBack ? (
            <Button
              onClick={handleBack}
              disabled={disableBack}
              variant="outline"
              size="sm"
              ariaLabel={backLabel}
              icon={<ArrowLeftIcon className="h-5 w-5" />}
            >
              <span className="sr-only">{backLabel}</span>
            </Button>
          ) : (
            <div />
          )}
          <Button
            onClick={handleContinue}
            disabled={disableContinue}
            variant="primary"
            size="sm"
            icon={<ArrowRightIcon className="h-4 w-4" />}
            iconPosition="right"
          >
            {continueLabel}
          </Button>
        </div>
      </div>

      {/* sm+: inline at bottom of content */}
      <div className="mt-16 hidden border-t border-oat pt-6 sm:block">
        {continueHint && (
          <p className="mb-2 ml-auto max-w-md text-left text-base text-soil">
            {continueHint}
          </p>
        )}
        <div className="flex items-center justify-between">
          {showBack ? (
            <Button
              onClick={handleBack}
              disabled={disableBack}
              variant="text"
              size="sm"
              icon={<ArrowLeftIcon className="h-4 w-4" />}
            >
              {backLabel}
            </Button>
          ) : (
            <div />
          )}
          <Button
            onClick={handleContinue}
            disabled={disableContinue}
            variant={disableContinue ? "outline" : "primary"}
          >
            {continueLabel}
          </Button>
        </div>
      </div>
    </>
  );
}
